import type {
  LicenseDto,
  MarketParticipantOrganisationResponse,
  MarketPartyRegistrationDto,
  MarketSegment,
  ProductKind,
  ProvidedService,
  SubUnitDto,
} from '#pie/internal/api/pie-bff';

export type MarketPartyRegistration = Omit<
  MarketPartyRegistrationDto,
  'status' | 'id'
> & {
  status?:
    | MarketPartyRegistrationDto['status']
    | MarketParticipantOrganisationResponse['status'];
  id?: //deze id's zijn gelijk aan elkaar
    | MarketPartyRegistrationDto['mprId']
    | NonNullable<MarketParticipantOrganisationResponse['organisation']>['id'];
  dynamicsId?: MarketParticipantOrganisationResponse['dynamicsId'];
};

const mapProduct = (product: string | undefined): ProductKind | undefined => {
  if (product === 'Electricity') {
    return '_23';
  }

  if (product === 'Gas') {
    return '_27';
  }

  return undefined;
};

const mapMarketSegment = (
  marketSegment: string | undefined,
): MarketSegment | undefined => {
  if (marketSegment === 'Small' || marketSegment === 'SML') {
    return 'SML';
  }

  if (marketSegment === 'Large' || marketSegment === 'LRG') {
    return 'LRG';
  }

  return undefined;
};

const mapLicense = (
  license: NonNullable<
    NonNullable<
      MarketParticipantOrganisationResponse['organisation']
    >['licenses']
  >[number],
): LicenseDto => ({
  assigmentAuthorityName: license.assignmentAuthority,
  endDate: license.endDate,
  mrId: license.id,
  name: license.name,
  providedServices: [
    ...new Set(
      license.providedServices.map(
        (providedService) => providedService as ProvidedService,
      ),
    ),
  ],
  referenceDescription: license.referenceDescription,
  startDate: license.startDate,
});

const mapSubUnit = (
  subUnit: NonNullable<
    MarketParticipantOrganisationResponse['subUnits']
  >[number],
): SubUnitDto => ({
  appearances: [
    ...new Set(
      subUnit.serviceCategories.flatMap(({ providedService }) =>
        providedService ? [providedService as ProvidedService] : [],
      ),
    ),
  ],
  eanCode: subUnit.eanCode,
  endDate: subUnit.validEndDate,
  iban: subUnit.iban,
  mrId: subUnit.id,
  name: subUnit.name,
  startDate: subUnit.validStartDate,
  vat: subUnit.vat,
});

export const toMarketPartyRegistration = (
  marketParty:
    | MarketPartyRegistrationDto
    | MarketParticipantOrganisationResponse,
): MarketPartyRegistration => {
  if (!('eanCode' in marketParty)) {
    return marketParty;
  }

  return {
    chamberOfCommerceNumber: marketParty.organisation?.chamberOfCommerceNumber,
    city: marketParty.city,
    contactValidationDate: marketParty.contactValidationDate,
    country: marketParty.country,
    houseNumber: marketParty.houseNumber,
    houseNumberExtension: marketParty.houseNumberExtension,
    licenses: marketParty.organisation?.licenses?.map(mapLicense),
    marketParticipantEanCode: marketParty.eanCode,
    marketParticipantStartDate: marketParty.validStartDate,
    marketRole: marketParty.type,
    id: marketParty.id,
    serviceCategories: marketParty.subUnits?.flatMap((subUnit) =>
      subUnit.serviceCategories.map((serviceCategory) => ({
        marketSegment: mapMarketSegment(serviceCategory.marketSegment),
        product: mapProduct(serviceCategory.product),
        providedService: serviceCategory.providedService as ProvidedService,
        mrId: serviceCategory.id,
      })),
    ),
    statutoryName: marketParty.organisation?.statutoryName,
    status: marketParty.status,
    streetName: marketParty.streetName,
    subUnits: marketParty.subUnits?.map(mapSubUnit),
    zipCode: marketParty.zipCode,
    dynamicsId: marketParty.dynamicsId,
  };
};
